/**
 * Data access for the Referrals feature.
 *
 *   GET {ServerRoutes.referrals}?page&limit&search       -> users by referral count
 *   GET {ServerRoutes.userTree}/{userId}/tree?page&limit -> direct referrals
 *
 * Both return the same envelope: { parent, items, meta, links }.
 */
import axios from "./axios-instance";
import { ServerRoutes } from "../routes";
import { ReferralUser, ReferralUsersResponse } from "../types";

export interface ReferralListParams {
  page: number;
  limit: number;
  search?: string;
}

export interface UserReferralsParams {
  page: number;
  limit: number;
}

/** Users ordered by referral count, most referrals first. */
export async function fetchUsersByReferrals(
  params: ReferralListParams,
): Promise<ReferralUsersResponse> {
  const query = new URLSearchParams();
  query.append("page", String(params.page));
  query.append("limit", String(params.limit));
  if (params.search?.trim()) query.append("search", params.search.trim());

  const response = await axios.get<ReferralUsersResponse>(
    `${ServerRoutes.referrals}?${query.toString()}`,
  );
  return response.data;
}

/** Direct referrals of a single user (plus that user's own referrer). */
export async function fetchUserReferrals(
  userId: number,
  params: UserReferralsParams,
): Promise<ReferralUsersResponse> {
  const query = new URLSearchParams();
  query.append("page", String(params.page));
  query.append("limit", String(params.limit));

  const response = await axios.get<ReferralUsersResponse>(
    `${ServerRoutes.userTree}/${userId}/tree?${query.toString()}`,
  );
  return response.data;
}

const COUNT_KEYS = [
  "referralCount",
  "referralsCount",
  "totalReferrals",
  "currentReferrals",
  "directReferralsCount",
  "childrenCount",
];

/**
 * The by-referrals endpoint is ordered by referral count, but the field name
 * carrying that count is not pinned down yet — accept the usual spellings.
 */
export function getReferralCount(user: ReferralUser): number | undefined {
  const raw = user as unknown as Record<string, unknown>;
  for (const key of COUNT_KEYS) {
    const value = raw[key];
    if (typeof value === "number") return value;
  }
  return undefined;
}
